import { Injectable, Type } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ProjectsService } from '@front-end/core/services';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  finalize,
  first,
  forkJoin,
  from,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { NgxAuthService } from 'ngx-auth-utils';
import { CashFund, CashFundsService, Identity } from '@libs/core/api';
import { FundCashPurchaseModalComponent } from './components/fund-cash-purchase-modal/fund-cash-purchase-modal.component';
import { FundCashPurchaseFormModalComponent } from './components/fund-cash-purchase-form-modal/fund-cash-purchase-form-modal.component';
import { FundCashPurchaseConfirmDraftPage } from './components/fund-cash-purchase-confirm-draft/fund-cash-purchase-confirm-draft.component';
import { Router } from '@angular/router';
import { FundCashPaymentModalComponent } from './components/fund-cash-payment-modal/fund-cash-payment-modal.component';
import { FundCashPurchaseSendModalComponent } from './components/fund-cash-purchase-send-modal/fund-cash-purchase-send-modal.component';
import { FundCashPurchaseConfirmTransfertPage } from './components/fund-cash-purchase-confirm-transfer/fund-cash-purchase-confirm-transfer.component';
import { ComponentProps, OverlayEventDetail } from '@ionic/core';
import { FundCashTransferDetailModalComponent } from './components/fund-cash-transfer-detail-modal/fund-cash-transfer-detail-modal.component';
import { FundCashPurchaseDetailModalComponent } from './components/fund-cash-purchase-detail-modal/fund-cash-purchase-detail-modal.component';

@Injectable({
  providedIn: 'root',
})
export class FundCashPurchaseService {
  readonly project$ = this.projectService.selectedProject$.pipe(first());
  readonly identity$: Observable<Identity> = this.authService.state.pipe(
    first()
  );
  readonly openedModal$ = new BehaviorSubject<HTMLIonModalElement | null>(null);

  public readonly refreshLists$ = new BehaviorSubject<void>(void 0);

  constructor(
    private projectService: ProjectsService,
    private modalController: ModalController,
    private authService: NgxAuthService,
    private router: Router,
    private cashFundsService: CashFundsService
  ) {}

  refreshLists() {
    this.refreshLists$.next();
  }

  openFundCashPurchaseModal(): Observable<void> {
    if (localStorage.getItem('hideFundCashGuide') === 'true') {
      return this.openPurchaseFormModal();
    }

    const closePreviousModal$ = this.openedModal$.pipe(
      first(),
      switchMap((modal) => {
        if (modal) {
          return from(modal.dismiss());
        }
        return from([null]);
      })
    );

    return closePreviousModal$.pipe(
      switchMap(() => forkJoin([this.project$, this.identity$])),
      switchMap(([project, identity]) => {
        return from(
          this.modalController.create({
            component: FundCashPurchaseModalComponent,
            componentProps: {
              project: project,
              identity: identity,
            },
          })
        ).pipe(
          tap((modal) => this.openedModal$.next(modal)),
          tap((modal) => modal.present()),
          switchMap((modal) => from(modal.onWillDismiss())),
          tap(() => this.openedModal$.next(null)),
          switchMap((result) => {
            if (result?.data?.action === 'create') {
              return this.openPurchaseFormModal();
            }
            return EMPTY;
          })
        );
      })
    );
  }

  openPurchaseFormModal(cashFund?: CashFund): Observable<void> {
    return forkJoin([this.project$, this.identity$]).pipe(
      switchMap(([project, identity]) => {
        return from(
          this.modalController.create({
            component: FundCashPurchaseFormModalComponent,
            componentProps: {
              project: project,
              identity: identity,
              cashFund: cashFund,
            },
          })
        ).pipe(
          tap((modal) => this.openedModal$.next(modal)),
          tap((modal) => modal.present()),
          switchMap((modal) => from(modal.onWillDismiss())),
          tap(() => this.openedModal$.next(null)),
          switchMap((result) => {
            this.refreshLists();
            if (result?.role === 'create') {
              return this.openPaymentStatus(result.data.id);
            }
            if (result?.role === 'draft') {
              return this.openConfirmSaveDraft();
            }
            if (result?.role === 'transfer') {
              return this.openConfirmTransfer();
            }
            return EMPTY;
          })
        );
      })
    );
  }

  private presentModal<T>(
    component: Type<T>,
    componentProps?: ComponentProps<T>
  ): Observable<void> {
    return defer(() =>
      this.modalController.create({ component, componentProps })
    ).pipe(
      tap((modal) => {
        this.openedModal$.next(modal);
        modal.present();
      }),
      switchMap(() => of(undefined)),
      finalize(() => this.openedModal$.next(null))
    );
  }

  openConfirmSaveDraft(): Observable<void> {
    return this.presentModal(FundCashPurchaseConfirmDraftPage);
  }

  openConfirmTransfer(): Observable<void> {
    return this.presentModal(FundCashPurchaseConfirmTransfertPage);
  }

  openPaymentStatus(id: number): Observable<void> {
    return from(
      this.modalController.create({
        component: FundCashPaymentModalComponent,
        backdropDismiss: false,
        componentProps: {
          id,
        },
      })
    ).pipe(
      tap((modal) => {
        this.openedModal$.next(modal);
        modal.present();
      }),
      switchMap((modal) => from(modal.onWillDismiss())),
      tap(() => this.openedModal$.next(null)),
      switchMap((result) => {
        if (result.data === 'paymentSuccessful') {
          return this.openPurchaseSendModal(id);
        }
        return of(undefined);
      })
    );
  }

  openPurchaseSendModal(id: number): Observable<void> {
    return forkJoin([
      this.project$,
      this.identity$,
      this.cashFundsService.getCashFund({ cashFundId: id }),
    ]).pipe(
      switchMap(([project, identity, cashFund]) => {
        return from(
          this.modalController.create({
            component: FundCashPurchaseSendModalComponent,
            backdropDismiss: false,
            componentProps: {
              id,
              project,
              identity,
              cashFund,
            },
          })
        ).pipe(
          tap((modal) => {
            this.openedModal$.next(modal);
            modal.present();
          }),
          switchMap((modal) => from(modal.onWillDismiss())),
          tap(() => this.openedModal$.next(null)),
          switchMap((result) => {
            if (result.data === 'finalized') {
              this.router.navigate(['home', 'fund-cash', 'finalized']);
            } else if (result.data === 'draft') {
              this.router.navigate(['home', 'fund-cash', 'draft']);
            }
            this.refreshLists();
            return of(undefined);
          })
        );
      })
    );
  }

  openPurchaseDetailModal(id: number): Observable<OverlayEventDetail> {
    return forkJoin([this.project$, this.identity$]).pipe(
      switchMap(([project, identity]) => {
        return from(
          this.modalController.create({
            component: FundCashPurchaseDetailModalComponent,
            backdropDismiss: false,
            componentProps: {
              id,
              project,
              identity,
            },
          })
        ).pipe(
          tap((modal) => {
            this.openedModal$.next(modal);
            modal.present();
          }),
          switchMap((modal) => from(modal.onWillDismiss())),
          tap(() => this.openedModal$.next(null))
        );
      })
    );
  }

  openTransferApprovalModal(cashFund: CashFund): Observable<void> {
    return from(
      this.modalController.create({
        component: FundCashTransferDetailModalComponent,
        componentProps: {
          purchase: cashFund,
        },
      })
    ).pipe(
      tap((modal) => {
        this.openedModal$.next(modal);
        modal.present();
      }),
      switchMap((modal) => from(modal.onWillDismiss())),
      tap(() => this.openedModal$.next(null)),
      tap((result) => {
        if (result?.role === 'transfer-status') {
          this.refreshLists();
        }
      }),
      switchMap(() => EMPTY)
    );
  }
}
